import { Option } from "@inrev/core";
import { useOnClickOutside } from "@portal/hooks/useOnClickOutside";
import { useOnKeyOutside } from "@portal/hooks/useOnKeyOutside";
import { type FC, type HTMLAttributes, type ReactNode, useId, useRef } from "react";
import { cn } from "../../lib/utils";
import { Button, Icon, Text } from "../../primitives";
import { trackingAttrs } from "../../utils";

const isModalBackdropOutsideClick = (target: Element) =>
	target.closest('[data-testid="modal-backdrop"]') !== null;

const isAriaControlsToggleOutsideClick = (target: Element, drawerId: string) => {
	const control = target.closest("[aria-controls]");
	if (Option.isNone(control)) return false;
	const controlledIds = control.getAttribute("aria-controls")?.split(/\s+/) ?? [];
	return controlledIds.includes(drawerId);
};

export type SideDrawerProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
	/** Drawer element id; a trigger with `aria-controls={id}` is not treated as an outside click. */
	id: string;
	isOpen: boolean;
	onClose: () => void;
	/** Header title. Names the drawer via `aria-labelledby` when open. */
	title: string;
	/** Accessible name for the header close control. */
	closeLabel: string;
	shouldIgnoreOutsideClick?: (target: Element) => boolean;
	children: ReactNode;
	testId?: string;
	trackingId?: string;
};

/**
 * Left-edge drawer that fills the nearest full-height `relative` ancestor. Keep at most one drawer
 * open per host at a time; both may stay mounted while closed.
 */
export const SideDrawer: FC<SideDrawerProps> = ({
	isOpen,
	onClose,
	title,
	closeLabel,
	children,
	shouldIgnoreOutsideClick,
	className,
	trackingId,
	testId = "side-drawer",
	id,
	...rest
}) => {
	const titleId = useId();
	const rootRef = useRef<HTMLDivElement>(null);

	const handleOutsideInteraction = (event: Event) => {
		if (!(event.target instanceof Element)) return;
		const target = event.target;
		if (isModalBackdropOutsideClick(target)) return;
		if (isAriaControlsToggleOutsideClick(target, id)) return;
		if (shouldIgnoreOutsideClick?.(target)) return;
		onClose();
	};

	useOnClickOutside(rootRef, handleOutsideInteraction, isOpen);
	useOnKeyOutside(rootRef, handleOutsideInteraction, isOpen);

	return (
		<div
			ref={rootRef}
			id={id}
			role="dialog"
			data-testid={testId}
			aria-hidden={!isOpen}
			aria-labelledby={isOpen ? titleId : undefined}
			className={cn(
				"absolute inset-y-0 left-0 z-40 flex w-[360px] flex-col border-r border-bc-default bg-surface-base shadow-xl transition-transform duration-300",
				isOpen ? "translate-x-0" : "-translate-x-full invisible pointer-events-none",
				className,
			)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			{isOpen && (
				<>
					<Button
						variant="ghost"
						onClick={onClose}
						aria-label={closeLabel}
						data-testid={`${testId}-close`}
						className="flex-col h-auto items-stretch whitespace-normal border-b border-bc-default py-3 text-left hover:bg-surface-light"
						trailingIcon={
							<span className="inline-flex h-6 w-6 items-center justify-end rounded-md">
								<Icon type="x-mark" size="mini" color="secondary" />
							</span>
						}
					>
						<Text id={titleId} type="body-strong" color="primary" className="flex-1">
							{title}
						</Text>
					</Button>
					{children}
				</>
			)}
		</div>
	);
};
